import type { KeyboardEvent, ReactNode } from 'react';

export type TableColumnType =
  | 'string'
  | 'integer'
  | 'long'
  | 'object'
  | 'decimal'
  | 'id'
  | 'double'
  | 'date'
  | 'percent'
  | 'group'
  | 'user'
  | 'band';

export type TableDataType = string | number | undefined | null;

export type SortDirection = 'asc' | 'desc';

export interface TableColumn {
  type: TableColumnType;
  label: string;
  key: string;
  sortable?: boolean; // defaults to true
  columns?: TableColumn[]; // nested columns for group-type headers
}

export interface ColumnGroup {
  label: string;
  colSpan: number;
}

export type TableRow = TableDataType[];

export interface TableToolbarProps {
  loading: boolean | undefined;
  isSearchEmpty: boolean;
  searchValue: string | undefined;
  searchPlaceholder: string;
  onSearchChange: ((value: string) => void) | undefined;
  onSearchKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onSearchSubmit: () => void;
  onExportClick: () => void;
  actions?: ReactNode;
}

export interface TableProps {
  columns: TableColumn[];
  rows: TableRow[];
  totalCount: number;

  // Sort (controlled, BE-driven). 3-state cycle: asc → desc → cleared
  sortColumn?: string;
  sortDirection?: SortDirection;
  onSortChange?: (
    column: string | undefined,
    direction: SortDirection | undefined
  ) => void;

  // Pagination (controlled, BE-driven)
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;

  // Search (controlled, BE-driven)
  searchValue?: string;
  /**
   * The search the rows answer, which is only what the reader submitted. The
   * empty state names this rather than `searchValue`, so typing a new search
   * does not relabel the result of the last one.
   */
  searchTerms?: string;
  onSearchChange?: (value: string) => void;
  onSearchSubmit?: (value: string) => void;

  // Button handlers
  onDrilldown?: (id: string | number) => void;
  onExportToCSV: () => Promise<void>;

  // Grouped column headers (optional)
  columnGroups?: ColumnGroup[];

  // Bordered cells — adds 1px #D4D4D4 border to every cell
  borderedCells?: boolean;

  /** Keys of text columns aligned left, as the first column always is. */
  leftAlignedColumns?: string[];

  /**
   * Nested rows by their parent's id. A row with children gets a toggle that
   * shows them beneath it, indented. See `buildRowTree`.
   */
  childRows?: Map<string | number, TableRow[]>;

  /**
   * Draws a cell itself. Returning `undefined` falls back to the formatted
   * value, so a page only handles the columns it needs to.
   */
  /** Extra controls shown on the toolbar's right, before the export button. */
  toolbarActions?: ReactNode;
  /** Shown under the toolbar, above the column headers. */
  toolbarCaption?: ReactNode;
  /**
   * Keeps the toolbar in place while rows load, for tables whose toolbar
   * controls start the reload (e.g. a picker for what the table shows).
   */
  keepToolbarWhileLoading?: boolean;
  /** Names the sideways-scrolling rows, making them a keyboard-reachable region. */
  scrollRegionLabel?: string;
  renderCell?: (
    value: TableDataType,
    column: TableColumn,
    row: TableRow,
    /** How deeply the row is nested: 0 for a top-level row. */
    depth: number
  ) => ReactNode | undefined;

  // Loading state
  loading?: boolean;

  // Error state
  error?: Error | null;
}

export const EXPORT_ERROR_MESSAGE = 'Unable to export. Please try again.';
export const DATA_ERROR_MESSAGE =
  'Unable to load. Try refreshing the page or clearing your cookies.';
export const DATA_UNAVAILABLE_MESSAGE = 'Data not yet available.';
